import { AVATAR_IDS, PAINT_IDS, SUIT_IDS, type ArmorChoice, type AvatarId, type PaintId, type SuitId } from '@qoobd/rules';

/** The three suits. Models are optimized glTF files in /models. */
/** `visor`: where the face sits on the normalised suit (1.8 m tall, feet at -1.15, facing +Z). */
export const SUITS: Record<SuitId, { name: string; blurb: string; file: string; visor: { y: number; z: number; size: number } }> = {
  vanguard: { name: 'Vanguard', blurb: 'Heavy industrial plate', file: 'models/vanguard.glb', visor: { y: 0.49, z: 0.2, size: 0.155 } },
  wraith: { name: 'Wraith', blurb: 'Light scout suit', file: 'models/wraith.glb', visor: { y: 0.51, z: 0.125, size: 0.105 } },
  salvager: { name: 'Salvager', blurb: 'Weathered maintenance rig', file: 'models/salvager.glb', visor: { y: 0.45, z: 0.305, size: 0.17 } },
};

/**
 * Plate paints. "Gunmetal" is the suit as designed; the rest tint the plates. Light strips
 * always show the side colour (amber for you, ice for the rival), never the paint, so sides stay
 * readable. Bright reds and cyans are left out: those mean threat and objective.
 */
export const PAINTS: Record<PaintId, { name: string; hex: number | null }> = {
  gunmetal: { name: 'Gunmetal', hex: null },
  graphite: { name: 'Graphite', hex: 0x2a2c30 },
  bone: { name: 'Bone', hex: 0xd9d2c3 },
  rust: { name: 'Rust', hex: 0x8a4b2a },
  olive: { name: 'Olive', hex: 0x5a6142 },
  navy: { name: 'Navy', hex: 0x2b3a55 },
  copper: { name: 'Copper', hex: 0x9c6b3f },
  sand: { name: 'Sand', hex: 0xb49a6a },
  teal: { name: 'Teal', hex: 0x2f5d5a },
  violet: { name: 'Violet', hex: 0x4b3b6b },
};

/**
 * Preset avatars: the face behind the visor. Each has a "hollow" version used only by mirror
 * rooms, for a quarter of a second.
 */
export const AVATARS: Record<AvatarId, { name: string }> = {
  kestrel: { name: 'Kestrel' },
  anvil: { name: 'Anvil' },
  wren: { name: 'Wren' },
  ember: { name: 'Ember' },
  sage: { name: 'Sage' },
  brick: { name: 'Brick' },
  unit9: { name: 'Unit 9' },
  vega: { name: 'Vega' },
};
export const avatarFile = (id: AvatarId, hollow = false) => `avatars/${id}${hollow ? '-hollow' : ''}.webp`;

export function randomArmor(rand: () => number = Math.random): ArmorChoice {
  const pick = <T,>(list: readonly T[]) => list[Math.floor(rand() * list.length)]!;
  return { suit: pick(SUIT_IDS), paint: pick(PAINT_IDS), avatar: pick(AVATAR_IDS) };
}

export { SUIT_IDS, PAINT_IDS, AVATAR_IDS };
export type { ArmorChoice, AvatarId, PaintId, SuitId };
